import React from 'react';
import { Box, CardContent, Typography, Divider, LinearProgress } from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CheckCircle as WinIcon,
  RemoveCircle as DrawIcon,
  Cancel as LossIcon,
} from '@mui/icons-material';
import { C } from './constants';
import { GlowCard, StatBar } from './ui';
import { winRate } from './utils';

/**
 * MatchPerformanceCard — wins/draws/losses, win rate bar, goal diff.
 *
 * Props:
 *   gameStats — { total, wins, draws, losses, goalsScored, goalsConceded }
 */
const MatchPerformanceCard = ({ gameStats, delay = 0 }) => {
  const wRate   = winRate(gameStats.wins, gameStats.total);
  const goalDiff = gameStats.goalsScored - gameStats.goalsConceded;

  return (
    <GlowCard delay={delay} accent={C.gold} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>

        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <TrophyIcon sx={{ color: C.gold, fontSize: 20 }} />
          <Typography sx={{ fontFamily: 'Syne', fontWeight: 700, color: C.text, fontSize: '1rem' }}>
            Performance des matchs
          </Typography>
        </Box>

        {/* Win rate bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
            <Typography sx={{ fontFamily: 'DM Sans', color: C.muted, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Taux de victoire
            </Typography>
            <Typography sx={{ fontFamily: 'Syne', fontWeight: 700, color: C.gold, fontSize: '0.9rem' }}>
              {wRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={wRate}
            sx={{
              height: 8, borderRadius: 99, background: C.border,
              '& .MuiLinearProgress-bar': {
                borderRadius: 99,
                background: `linear-gradient(90deg, ${C.gold}, ${C.amber})`,
              },
            }}
          />
        </Box>

        {/* W / D / L */}
        {[
          { label: 'Victoires', value: gameStats.wins,   color: C.emerald, icon: <WinIcon  sx={{ fontSize: 16 }} /> },
          { label: 'Nuls',      value: gameStats.draws,  color: C.amber,   icon: <DrawIcon sx={{ fontSize: 16 }} /> },
          { label: 'Défaites',  value: gameStats.losses, color: C.rose,    icon: <LossIcon sx={{ fontSize: 16 }} /> },
        ].map(({ label, value, color, icon }) => (
          <Box key={label} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color }}>
                {icon}
                <Typography sx={{ fontFamily: 'DM Sans', color, fontSize: '0.82rem' }}>{label}</Typography>
              </Box>
              <Typography sx={{ fontFamily: 'Syne', fontWeight: 700, color: C.text, fontSize: '0.9rem' }}>{value}</Typography>
            </Box>
            <StatBar value={value} max={gameStats.total || 1} color={color} />
          </Box>
        ))}

        <Divider sx={{ my: 2, borderColor: C.border }} />

        {/* Goal stats */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {[
            { val: gameStats.goalsScored,                                  color: C.emerald, label: 'Buts marqués'   },
            { val: gameStats.goalsConceded,                                 color: C.rose,    label: 'Buts encaissés' },
            { val: `${goalDiff >= 0 ? '+' : ''}${goalDiff}`,               color: C.accent,  label: 'Différence'     },
          ].map(({ val, color, label }, i) => (
            <React.Fragment key={label}>
              {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: C.border }} />}
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography sx={{ fontFamily: 'Syne', fontWeight: 800, color, fontSize: '1.4rem' }}>{val}</Typography>
                <Typography sx={{ fontFamily: 'DM Sans', color: C.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {label}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>

      </CardContent>
    </GlowCard>
  );
};

export default MatchPerformanceCard;
